import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { galleryApi } from '../../services/api';

const C = {
  card:'#ffffff', border:'#f0e4d8', border2:'#f5ede4',
  text1:'#1a0f00', text3:'#6b4c35', text4:'#a07858',
  accent:'#c96428', accentL:'#fff4ee', accentB:'#f0d0b0',
};

const AdminGallery = () => {
  const [images,   setImages]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [uploading,setUploading]= useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [caption,  setCaption]  = useState('');
  const [preview,  setPreview]  = useState(null);
  const [file,     setFile]     = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef();

  const fetchImages = async () => {
    setLoading(true);
    try { const res = await galleryApi.getAll(); setImages(res.data.data || []); }
    catch { toast.error('Failed to load gallery'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchImages(); }, []);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (!f || !f.type.startsWith('image/')) return;
    setFile(f);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const handleUpload = async () => {
    if (!file) return toast.error('Select an image first');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      if (caption) fd.append('caption', caption);
      await galleryApi.upload(fd);
      toast.success('Image uploaded!');
      setFile(null); setPreview(null); setCaption('');
      fetchImages();
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    try { await galleryApi.delete(id); toast.success('Image deleted'); setDeleteId(null); fetchImages(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:24 }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <h1 style={{ fontFamily:'"Playfair Display",serif', fontSize:26, fontWeight:800, color:C.text1, margin:0 }}>Gallery</h1>
          <p style={{ color:C.text4, fontSize:13, marginTop:4 }}>{images.length} photos uploaded</p>
        </div>
        <div style={{ background:C.accentL, border:`1px solid ${C.accentB}`, borderRadius:12, padding:'10px 18px', textAlign:'center' }}>
          <p style={{ color:C.text4, fontSize:10, fontWeight:600, textTransform:'uppercase', letterSpacing:0.5, margin:0 }}>Total Photos</p>
          <p style={{ color:C.accent, fontSize:22, fontWeight:800, margin:'2px 0 0' }}>{images.length}</p>
        </div>
      </div>

      {/* Upload panel */}
      <div style={{ background:C.card, borderRadius:18, border:`1px solid ${C.border}`, padding:28, boxShadow:'0 2px 12px rgba(0,0,0,0.05)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20, paddingBottom:16, borderBottom:`1px solid ${C.border2}` }}>
          <div style={{ width:40, height:40, borderRadius:12, background:C.accentL, border:`1.5px solid ${C.accentB}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18 }}>📷</div>
          <div>
            <p style={{ fontFamily:'"Playfair Display",serif', fontSize:16, fontWeight:700, color:C.text1, margin:0 }}>Upload New Photo</p>
            <p style={{ color:C.text4, fontSize:12, margin:'2px 0 0' }}>Drag & drop or click to select</p>
          </div>
        </div>

        <div style={{ display:'flex', gap:20, flexWrap:'wrap' }}>
          {/* Drop zone */}
          <div
            onClick={() => fileRef.current.click()}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            style={{
              width:180, height:140, borderRadius:14, overflow:'hidden', cursor:'pointer',
              border:`2px dashed ${dragOver ? C.accent : C.border}`,
              background: dragOver ? C.accentL : '#fdfaf7',
              display:'flex', alignItems:'center', justifyContent:'center',
              transition:'all 0.2s', flexShrink:0,
            }}>
            {preview ? (
              <img src={preview} alt="Preview" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
            ) : (
              <div style={{ textAlign:'center', padding:16 }}>
                <div style={{ fontSize:36, marginBottom:8 }}>🖼️</div>
                <p style={{ color:C.text4, fontSize:12, margin:0, fontWeight:500 }}>Click or drag image here</p>
                <p style={{ color:'#c8a890', fontSize:10, margin:'4px 0 0' }}>PNG, JPG, WEBP</p>
              </div>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" style={{ display:'none' }} onChange={handleFileChange}/>

          {/* Caption + upload */}
          <div style={{ flex:1, minWidth:200, display:'flex', flexDirection:'column', gap:12 }}>
            <div>
              <label style={{ display:'block', color:C.text3, fontSize:11, fontWeight:700, marginBottom:6, textTransform:'uppercase', letterSpacing:0.5 }}>Caption (Optional)</label>
              <input placeholder="e.g. Our cozy corner seating..."
                value={caption} onChange={e => setCaption(e.target.value)}
                style={{ width:'100%', padding:'10px 14px', borderRadius:10, fontSize:14, border:`1.5px solid ${C.border}`, background:'#fffaf7', color:C.text1, fontFamily:'inherit', outline:'none', boxSizing:'border-box' }}
                onFocus={e => { e.target.style.borderColor=C.accent; e.target.style.boxShadow=`0 0 0 3px rgba(201,100,40,0.1)`; }}
                onBlur={e => { e.target.style.borderColor=C.border; e.target.style.boxShadow='none'; }}
              />
            </div>

            {file && (
              <div style={{ padding:'10px 14px', borderRadius:10, background:C.accentL, border:`1px solid ${C.accentB}`, fontSize:12, color:C.text3 }}>
                📄 {file.name} · {(file.size/1024).toFixed(0)} KB
              </div>
            )}

            <div style={{ display:'flex', gap:10 }}>
              <button onClick={handleUpload} disabled={!file||uploading}
                style={{ padding:'11px 24px', borderRadius:11, border:'none', fontSize:13, fontWeight:700, fontFamily:'inherit', cursor:!file||uploading?'not-allowed':'pointer', transition:'all 0.15s', background:!file||uploading?'#e8d5c4':'linear-gradient(135deg,#c96428,#9a4a1e)', color:'#fff', boxShadow:!file||uploading?'none':'0 4px 14px rgba(201,100,40,0.35)' }}>
                {uploading ? '⏳ Uploading...' : '📤 Upload Photo'}
              </button>
              {file && (
                <button onClick={() => { setFile(null); setPreview(null); setCaption(''); }}
                  style={{ padding:'11px 16px', borderRadius:11, border:`1.5px solid ${C.border}`, background:'#fff', color:C.text3, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Gallery grid */}
      {loading ? (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:16 }}>
          {Array(8).fill(0).map((_,i) => (
            <div key={i} style={{ aspectRatio:'1', borderRadius:16, background:'#f5ede4', animation:'pulse 1.5s infinite' }}/>
          ))}
        </div>
      ) : images.length === 0 ? (
        <div style={{ textAlign:'center', padding:'64px 24px', background:C.card, borderRadius:18, border:`1px solid ${C.border}` }}>
          <div style={{ fontSize:48, marginBottom:12 }}>🖼️</div>
          <p style={{ color:C.text3, fontSize:16, fontWeight:600, margin:0 }}>No photos yet</p>
          <p style={{ color:C.text4, fontSize:13, marginTop:6 }}>Upload your first photo to get started!</p>
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:16 }}>
          {images.map(img => (
            <motion.div key={img.id} layout
              whileHover={{ y:-4, boxShadow:'0 12px 40px rgba(0,0,0,0.15)' }}
              style={{ borderRadius:16, overflow:'hidden', position:'relative', aspectRatio:'1', boxShadow:'0 2px 12px rgba(0,0,0,0.08)', border:`1px solid ${C.border}`, cursor:'pointer' }}>
              <img
                src={img.image_url.startsWith('http') ? img.image_url : `http://localhost:5000${img.image_url}`}
                alt={img.caption||'Gallery'}
                style={{ width:'100%', height:'100%', objectFit:'cover', display:'block', transition:'transform 0.3s' }}
                onMouseOver={e => e.target.style.transform='scale(1.05)'}
                onMouseOut={e => e.target.style.transform='scale(1)'}
              />
              {/* Overlay */}
              <div className="gallery-overlay" style={{ position:'absolute', inset:0, background:'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 60%)', opacity:0, transition:'opacity 0.25s', display:'flex', flexDirection:'column', justifyContent:'flex-end', padding:14 }}>
                {img.caption && <p style={{ color:'#fff', fontSize:12, fontWeight:500, margin:'0 0 8px', lineHeight:1.4 }}>{img.caption}</p>}
                <button onClick={() => setDeleteId(img.id)}
                  style={{ padding:'6px 14px', borderRadius:8, background:'#ef4444', color:'#fff', border:'none', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'inherit', alignSelf:'flex-start' }}>
                  🗑️ Delete
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Delete confirm modal */}
      <AnimatePresence>
        {deleteId && (
          <div style={{ position:'fixed', inset:0, zIndex:50, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
            <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
              style={{ position:'absolute', inset:0, background:'rgba(26,15,0,0.55)', backdropFilter:'blur(4px)' }}
              onClick={() => setDeleteId(null)}/>
            <motion.div initial={{opacity:0,scale:0.9,y:20}} animate={{opacity:1,scale:1,y:0}} exit={{opacity:0,scale:0.9}}
              style={{ position:'relative', background:'#fff', borderRadius:20, padding:32, width:'100%', maxWidth:340, textAlign:'center', boxShadow:'0 24px 64px rgba(0,0,0,0.2)', border:`1px solid ${C.border}` }}>
              <div style={{ width:64, height:64, borderRadius:18, background:'#fee2e2', display:'flex', alignItems:'center', justifyContent:'center', fontSize:28, margin:'0 auto 16px' }}>🗑️</div>
              <h3 style={{ fontFamily:'"Playfair Display",serif', fontSize:18, fontWeight:700, color:C.text1, margin:'0 0 8px' }}>Delete Photo?</h3>
              <p style={{ color:C.text4, fontSize:13, margin:'0 0 24px' }}>This action cannot be undone.</p>
              <div style={{ display:'flex', gap:12 }}>
                <button onClick={() => setDeleteId(null)}
                  style={{ flex:1, padding:'11px 0', borderRadius:11, border:`1.5px solid ${C.border}`, background:'#fff', color:C.text3, fontSize:14, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteId)}
                  style={{ flex:1, padding:'11px 0', borderRadius:11, border:'none', background:'linear-gradient(135deg,#ef4444,#dc2626)', color:'#fff', fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:'inherit', boxShadow:'0 4px 14px rgba(239,68,68,0.35)' }}>
                  Delete
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <style>{`
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.55}}
        .gallery-overlay:hover{opacity:1!important}
        div:hover > .gallery-overlay{opacity:1}
      `}</style>
    </div>
  );
};

export default AdminGallery;
